"use client";

import { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import ScanBarcodeIcon from '@/components/icons/scan-barcode-icon';

const FORMATS = [
  'code_128', 'code_39', 'code_93', 'codabar', 'qr_code',
  'data_matrix', 'aztec', 'ean_8', 'ean_13', 'upc_a', 'upc_e', 'itf', 'pdf417',
];

// USB keyboard-wedge scanner threshold: chars must arrive within this window.
const USB_INTER_CHAR_MS = 80;
const USB_MIN_LENGTH = 4;

export default function BarcodeScanner({ onScan, onClose }: { onScan: (value: string) => void; onClose: () => void }) {
  const onScanRef = useRef(onScan);
  useLayoutEffect(() => { onScanRef.current = onScan; });

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const usbBuffer = useRef('');
  const usbTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  useEffect(() => {
    if (!supported) return;
    let active = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let detector: any;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const supportedFormats = await (window as any).BarcodeDetector.getSupportedFormats();
        const formats = FORMATS.filter(f => supportedFormats.includes(f));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        detector = new (window as any).BarcodeDetector({ formats: formats.length ? formats : ['qr_code'] });
        scan();
      } catch (err: unknown) {
        const e = err as { name?: string; message?: string };
        if (e.name === 'NotAllowedError') setError('Camera access denied. Allow camera access in your browser settings.');
        else if (e.name === 'NotFoundError') setError('No camera found on this device.');
        else setError(e.message || 'Camera error');
      }
    }

    async function scan() {
      if (!active || !videoRef.current || videoRef.current.readyState < 2) {
        rafRef.current = requestAnimationFrame(scan);
        return;
      }
      try {
        const codes = await detector.detect(videoRef.current);
        if (codes.length > 0) { stop(); onScanRef.current(codes[0].rawValue); return; }
      } catch (_) {}
      if (active) rafRef.current = requestAnimationFrame(scan);
    }

    function stop() {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    }

    start();
    return stop;
  }, [supported]);

  // Window-level USB keyboard-wedge listener. Scanner sends chars fast; we
  // collect them until Enter arrives or the inter-char timer expires.
  const handleUsbKey = useCallback((e: KeyboardEvent) => {
    if (usbTimerRef.current) clearTimeout(usbTimerRef.current);
    if (e.key === 'Enter') {
      const value = usbBuffer.current.trim();
      usbBuffer.current = '';
      if (value.length >= USB_MIN_LENGTH) { onScanRef.current(value); }
      return;
    }
    if (e.key.length === 1) {
      usbBuffer.current += e.key;
      usbTimerRef.current = setTimeout(() => { usbBuffer.current = ''; }, USB_INTER_CHAR_MS);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleUsbKey);
    return () => window.removeEventListener('keydown', handleUsbKey);
  }, [handleUsbKey]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-end sm:items-center justify-center p-4">
      <div className="bg-[var(--bg-surface)] rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <ScanBarcodeIcon size={16} className="text-brand" />
            <span className="font-semibold text-[var(--text-1)] text-sm">Scan Barcode</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-full text-[var(--text-3)] hover:bg-[var(--bg-raised)] hover:text-[var(--text-2)]">
            <X size={18} />
          </button>
        </div>
        {!supported ? (
          <div className="p-6 text-center space-y-2">
            <ScanBarcodeIcon size={32} className="mx-auto text-[var(--text-3)]" />
            <p className="text-sm font-medium text-[var(--text-2)]">Camera scanning not supported</p>
            <p className="text-xs text-[var(--text-2)]">Use Safari on iOS 16.4+ or Chrome 88+. USB scanners still work — focus the text field and scan.</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center space-y-2">
            <p className="text-sm font-medium text-red-600">{error}</p>
            <button onClick={onClose} className="text-xs text-[var(--text-2)] underline">Close</button>
          </div>
        ) : (
          <div className="relative bg-black">
            <video ref={videoRef} className="w-full aspect-video object-cover" playsInline muted autoPlay />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative w-56 h-28">
                {[['top-0 left-0', 'border-t-2 border-l-2'], ['top-0 right-0', 'border-t-2 border-r-2'],
                  ['bottom-0 left-0', 'border-b-2 border-l-2'], ['bottom-0 right-0', 'border-b-2 border-r-2']
                ].map(([pos, border]) => (
                  <div key={pos} className={`absolute w-5 h-5 border-brand rounded-sm ${pos} ${border}`} />
                ))}
                {ready && <div className="absolute left-2 right-2 top-1/2 h-px bg-brand opacity-70 animate-pulse" />}
              </div>
            </div>
            <div className="absolute bottom-3 left-0 right-0 text-center">
              <span className="text-white text-xs bg-black/50 px-3 py-1 rounded-full">
                {ready ? 'Point at barcode' : 'Starting camera…'}
              </span>
            </div>
          </div>
        )}
        {supported && !error && (
          <div className="px-4 py-2 bg-[var(--bg-raised)] text-center">
            <p className="text-xs text-[var(--text-3)]">Tap outside or press × to cancel</p>
          </div>
        )}
      </div>
    </div>
  );
}
